import { uploadMerchantFile } from 'APIs/files/files';
import { MerchantFileUploadDocType } from 'APIs/files/files.types';
import { getIndustries } from 'APIs/industries/industries';
import { Industry } from 'APIs/industries/industries.types';
import { getMerchant, updateMerchantBusinessProfile } from 'APIs/merchants/merchants';
import { MerchantBusinessProfile, UpdateMerchantBusinessProfileRequest } from 'APIs/merchants/merchants.types';
import { downloadFile } from 'Components/Common/FileViewer';
import Spinner from 'Components/Spinner/Spinner';
import { LpBox } from 'Constants/styles';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import getApiErrorMessage from 'utils/getApiErrorMsg';

import BusinessProfileForm from './BusinessProfileForm';
import BusinessProfileView from './BusinessProfileView';
import { BusinessProfileHeader, EditBusinessProfile, MessageWrapper } from './styles';

type Props = {
  merchantId: string;
};

const UpdateBusinessProfile = ({ merchantId }: Props) => {
  const { apiBaseUri, apiAprilBaseUri } = useSelector((state: ReduxStateType) => ({
    apiBaseUri: state.apiBaseUri,
    apiAprilBaseUri: state.apiAprilBaseUri,
  }));

  const [businessProfile, setBusinessProfile] = useState<MerchantBusinessProfile | null>(null);
  const [industries, setIndustries] = useState<Array<Industry>>([]);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [updateErrorMsg, setUpdateErrorMsg] = useState<string>('');
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);

  const fetchBusinessProfile = useCallback(async () => {
    try {
      setIsFetching(true);
      const merchant = await getMerchant(apiAprilBaseUri, merchantId);
      setBusinessProfile(merchant.businessProfile);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setIsFetching(false);
    }
  }, [apiAprilBaseUri, merchantId]);

  const fetchIndustries = useCallback(async () => {
    try {
      const indList = await getIndustries(apiAprilBaseUri);
      setIndustries(indList);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  }, [apiAprilBaseUri]);

  const updateBusinessProfile = useCallback(
    async (data: UpdateMerchantBusinessProfileRequest): Promise<void> => {
      try {
        setIsUpdating(true);

        await updateMerchantBusinessProfile(apiBaseUri, merchantId, data);
        fetchBusinessProfile();
        setShowUpdateModal(false);
        toast.success('Successfully updated merchant business profile');
      } catch (error) {
        setUpdateErrorMsg(getApiErrorMessage(error));
      } finally {
        setIsUpdating(false);
      }
    },
    [apiBaseUri, fetchBusinessProfile, merchantId],
  );

  const handleFileUploads = useCallback(
    async (file: File, docType: MerchantFileUploadDocType): Promise<string | null> => {
      let fileId: string | null = null;
      try {
        const resp = await uploadMerchantFile(apiBaseUri, merchantId, 'MerchantOnboarding', docType, file);
        fileId = resp.limeFileId;
      } catch (error) {
        console.log(error);
      } finally {
        return fileId;
      }
    },
    [apiBaseUri, merchantId],
  );

  const handleDownloadFile = useCallback(
    async (fileId: string): Promise<{ fileUrl: string; fileType: string } | null> => {
      return await downloadFile(apiBaseUri, merchantId, fileId);
    },
    [apiBaseUri, merchantId],
  );

  const handleEditButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setShowUpdateModal(true);
  };

  useEffect(() => {
    if (!merchantId) {
      return;
    }
    fetchBusinessProfile();
  }, [fetchBusinessProfile, merchantId]);

  useEffect(() => {
    fetchIndustries();
  }, [fetchIndustries]);

  return (
    <LpBox>
      {isFetching && !businessProfile ? (
        <MessageWrapper>
          <Spinner color="#0016D1" height="25px" width="25px" />
        </MessageWrapper>
      ) : (
        <>
          <BusinessProfileHeader>
            <h3 className="mb-0">Business profile</h3>
            <EditBusinessProfile onClick={handleEditButtonClick}>Edit</EditBusinessProfile>
          </BusinessProfileHeader>

          {showUpdateModal && businessProfile && industries.length > 0 && (
            <BusinessProfileForm
              merchantBusinessProfile={businessProfile}
              isLoading={isUpdating}
              errorMsg={updateErrorMsg}
              industries={industries}
              updateBusinessProfile={updateBusinessProfile}
              handleFileUploads={handleFileUploads}
              downloadFile={handleDownloadFile}
              setShowUpdateModal={setShowUpdateModal}
            />
          )}

          <BusinessProfileView
            businessProfile={businessProfile}
            industries={industries}
            downloadFile={handleDownloadFile}
          />
        </>
      )}
    </LpBox>
  );
};

export default UpdateBusinessProfile;
